'use client';

import React, { useState } from 'react';
import { X, ShieldCheck, Tag, CheckCircle2, Loader2, Store } from 'lucide-react';

interface PromoRedeemModalProps {
  promoCode: {
    id: string;
    promo_code: string;
    promo_code_text?: string | null;
    offer?: { title: string; city: string };
  };
  onPartnerConfirm: (id: string) => Promise<void>;
  onClose: () => void;
}

export default function PromoRedeemModal({ promoCode, onPartnerConfirm, onClose }: PromoRedeemModalProps) {
  const [confirming, setConfirming] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  const handlePartnerConfirm = async () => {
    setConfirming(true);
    try {
      await onPartnerConfirm(promoCode.id);
      setConfirmed(true);
      // Auto-close after success animation
      setTimeout(() => {
        onClose();
      }, 1800);
    } catch {
      setConfirming(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center"
      style={{ background: 'rgba(0,0,0,0.82)', backdropFilter: 'blur(6px)' }}
      onClick={!confirming && !confirmed ? onClose : undefined}
    >
      <div
        className="w-full max-w-mobile rounded-t-[28px] overflow-hidden animate-slide-up"
        style={{
          background: 'linear-gradient(160deg, #1b1d23 0%, #1e2128 100%)',
          border: '1px solid rgba(124,179,66,0.25)',
          borderBottom: 'none',
          paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 28px)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top accent bar */}
        <div
          className="h-1 w-full"
          style={{ background: 'linear-gradient(90deg, #7CB342, #a3c44a, #7CB342)' }}
        />

        {/* Close button */}
        {!confirmed && (
          <div className="flex justify-end px-5 pt-4">
            <button
              onClick={onClose}
              disabled={confirming}
              className="w-8 h-8 rounded-full flex items-center justify-center transition-all duration-150 active:scale-90"
              style={{ background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.5)' }}
              aria-label="Schließen"
            >
              <X size={15} />
            </button>
          </div>
        )}

        {confirmed ? (
          /* ── Success State ── */
          <div className="flex flex-col items-center justify-center px-6 pt-6 pb-4 text-center">
            <div
              className="w-20 h-20 rounded-full flex items-center justify-center mb-4"
              style={{ background: 'rgba(124,179,66,0.15)', border: '2px solid rgba(124,179,66,0.4)' }}
            >
              <CheckCircle2 size={40} color="#7CB342" />
            </div>
            <h2 className="text-xl font-bold mb-2" style={{ color: '#fff' }}>
              Eingelöst! 🎉
            </h2>
            <p className="text-sm" style={{ color: 'rgba(255,255,255,0.6)' }}>
              Promo-Code wurde erfolgreich eingelöst und in deinen Buchungen gespeichert.
            </p>
          </div>
        ) : (
          /* ── Main Content ── */
          <div className="px-6 pt-2 pb-2">
            {/* Partner name */}
            <div className="text-center mb-5">
              <p className="text-xs font-semibold uppercase tracking-widest mb-1" style={{ color: 'rgba(124,179,66,0.8)' }}>
                Jetzt einlösen bei
              </p>
              <h2 className="text-xl font-bold leading-tight" style={{ color: '#fff' }}>
                {promoCode.offer?.title || 'Unserem Partner'}
              </h2>
              {promoCode.offer?.city && (
                <p className="text-sm mt-1" style={{ color: 'rgba(255,255,255,0.45)' }}>
                  {promoCode.offer.city}
                </p>
              )}
            </div>

            {/* Promo description text (new system) */}
            {promoCode.promo_code_text && (
              <div
                className="flex items-center justify-center gap-2 mb-4 px-4 py-2.5 rounded-xl"
                style={{ background: 'rgba(124,179,66,0.12)', border: '1px solid rgba(124,179,66,0.3)' }}
              >
                <Tag size={14} color="#7CB342" />
                <span className="text-sm font-bold" style={{ color: '#7CB342' }}>
                  {promoCode.promo_code_text}
                </span>
              </div>
            )}

            {/* Promo code display */}
            <div
              className="rounded-2xl p-5 mb-5 text-center relative overflow-hidden"
              style={{
                background: 'linear-gradient(135deg, rgba(124,179,66,0.12) 0%, rgba(163,196,74,0.06) 100%)',
                border: '1.5px dashed rgba(124,179,66,0.45)',
              }}
            >
              {/* Decorative dots */}
              <div
                className="absolute left-0 top-1/2 -translate-y-1/2 w-4 h-8 rounded-r-full"
                style={{ background: '#1b1d23', marginLeft: '-1px' }}
              />
              <div
                className="absolute right-0 top-1/2 -translate-y-1/2 w-4 h-8 rounded-l-full"
                style={{ background: '#1b1d23', marginRight: '-1px' }}
              />

              <div className="flex items-center justify-center gap-2 mb-2">
                <Tag size={14} color="rgba(124,179,66,0.7)" />
                <span className="text-xs font-semibold uppercase tracking-widest" style={{ color: 'rgba(124,179,66,0.7)' }}>
                  Promo-Code
                </span>
              </div>
              <p
                className="text-3xl font-black tracking-[0.18em] select-all"
                style={{ color: '#fff', letterSpacing: '0.18em' }}
              >
                {promoCode.promo_code}
              </p>
            </div>

            {/* Verified badge */}
            <div
              className="flex items-center gap-3 rounded-xl px-4 py-3 mb-6"
              style={{
                background: 'rgba(124,179,66,0.08)',
                border: '1px solid rgba(124,179,66,0.2)',
              }}
            >
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ background: 'rgba(124,179,66,0.15)' }}
              >
                <ShieldCheck size={20} color="#7CB342" />
              </div>
              <div>
                <p className="text-sm font-bold" style={{ color: '#fff' }}>
                  Verifizierter Account
                </p>
                <p className="text-xs mt-0.5" style={{ color: 'rgba(124,179,66,0.85)' }}>
                  Promo-Code genehmigt ✓
                </p>
              </div>
            </div>

            {/* Partner confirm button */}
            <div
              className="rounded-2xl p-4 mb-2"
              style={{
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.08)',
              }}
            >
              <p className="text-xs text-center mb-3" style={{ color: 'rgba(255,255,255,0.45)' }}>
                Partner / Freizeitlocation bestätigt Einlösung:
              </p>
              <button
                onClick={handlePartnerConfirm}
                disabled={confirming}
                className="w-full flex items-center justify-center gap-2.5 py-4 rounded-xl font-bold text-base transition-all duration-150 active:scale-[0.98]"
                style={{
                  background: confirming
                    ? 'rgba(124,179,66,0.4)'
                    : 'linear-gradient(135deg, #7CB342 0%, #a3c44a 100%)',
                  color: confirming ? 'rgba(255,255,255,0.6)' : '#1a1c22',
                  boxShadow: confirming ? 'none' : '0 4px 20px rgba(124,179,66,0.35)',
                }}
              >
                {confirming ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Wird bestätigt…
                  </>
                ) : (
                  <>
                    <Store size={18} />
                    Partner bestätigt Einlösung
                  </>
                )}
              </button>
            </div>

            <p className="text-[10px] text-center" style={{ color: 'rgba(255,255,255,0.25)' }}>
              Nur der Partner vor Ort drückt diesen Button
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
